import {
  Platform,
} from '@angular/cdk/platform';
import {
  AsyncPipe,
  NgTemplateOutlet,
} from '@angular/common';
import {
  HttpClientModule,
} from '@angular/common/http';
import {
  APP_INITIALIZER,
  DOCUMENT,
  EnvironmentProviders,
  inject,
  NgModule,
  Optional,
  provideEnvironmentInitializer,
  Provider,
} from '@angular/core';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MAT_RIPPLE_GLOBAL_OPTIONS,
  MatNativeDateModule,
} from '@angular/material/core';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
} from '@angular/material/dialog';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
} from '@angular/material/form-field';
import {
  MAT_TOOLTIP_DEFAULT_OPTIONS,
} from '@angular/material/tooltip';
import {
  BrowserModule,
} from '@angular/platform-browser';
import {
  BrowserAnimationsModule,
} from '@angular/platform-browser/animations';
import {
  ServiceWorkerModule,
} from '@angular/service-worker';
import {
  ErrorsHandlerModule,
} from '@des/services/errors-handler';
import {
  LOG_SERVER_LOGGER_CLIENT_OPTIONS,
} from '@des/services/logging';
import {
  airBoundExchangesStorageSync,
} from '@des/store/air-bound-exchanges';
import {
  airBoundExchangesSearchStorageSync,
} from '@des/store/air-bound-exchanges-search';
import {
  airBoundsStorageSync,
} from '@des/store/air-bounds';
import {
  airBoundsSearchStorageSync,
} from '@des/store/air-bounds-search';
import {
  airOfferConditionsStorageSync,
} from '@des/store/air-offer-conditions';
import {
  airOffersStorageSync,
} from '@des/store/air-offers';
import {
  airOffersAdvancedSearchStorageSync,
} from '@des/store/air-offers-advanced-search';
import {
  airOffersUpsellStorageSync,
} from '@des/store/air-offers-upsell';
import {
  baggagePoliciesStorageSync,
} from '@des/store/baggage-policies';
import {
  cartStorageSync,
} from '@des/store/cart';
import {
  checkTasksStorageSync,
} from '@des/store/check-tasks';
import {
  insuranceOffersStorageSync,
} from '@des/store/insurance-offers';
import {
  LocationStoreModule,
} from '@des/store/location';
import {
  MessagesBaseStoreModule,
} from '@des/store/messages';
import {
  orderStorageDeserializer,
} from '@des/store/order';
import {
  orderChangeStorageSync,
  OrderChangeStoreModule,
} from '@des/store/order-change';
import {
  paymentMethodsStorageSync,
} from '@des/store/payment-methods';
import {
  servicesCatalogueItemsStorageSync,
  ServicesCatalogueItemsStoreModule,
} from '@des/store/services-catalogue-items';
import {
  upgradeCatalogueItemsStorageSync,
} from '@des/store/upgrade-catalogue';
import {
  EffectsModule,
} from '@ngrx/effects';
import {
  routerReducer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import {
  Action,
  ActionReducer,
  createReducer,
  Store,
  StoreModule,
  USER_RUNTIME_CHECKS,
} from '@ngrx/store';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import {
  EVENT_TRACK_SERVICE_CONFIGURATION,
  EventTrackService,
} from '@o3r/analytics';
import {
  API_TOKEN,
  ApiFactoryService,
} from '@o3r/apis-manager';
import {
  PlaceholderModule,
  PlaceholderRequestStoreModule,
  provideCustomComponents,
} from '@o3r/components';
import {
  PlaceholderRulesEngineActionModule,
} from '@o3r/components/rules-engine';
import {
  configOverrideStorageSync,
  ConfigOverrideStoreModule,
  configurationAdapter,
  ConfigurationBaseService,
  ConfigurationBaseServiceModule,
  configurationReducerFeatures,
  configurationStorageSync,
  ConfigurationStoreModule,
} from '@o3r/configuration';
import {
  ConfigurationRulesEngineActionModule,
} from '@o3r/configuration/rules-engine';
import {
  AssetPathOverrideStoreModule,
  provideDynamicContent,
  provideRequestParameters,
  RequestParametersService,
} from '@o3r/dynamic-content';
import {
  AssetRulesEngineActionModule,
} from '@o3r/dynamic-content/rules-engine';
import {
  CUSTOM_LOCALIZATION_CONFIGURATION_TOKEN,
  LocalizationModule,
  LocalizationOverrideStoreModule,
  MESSAGE_FORMAT_CONFIG,
  translateLoaderProvider,
  TranslateMessageFormatLazyCompiler,
} from '@o3r/localization';
import {
  LocalizationRulesEngineActionModule,
} from '@o3r/localization/rules-engine';
import {
  LoggerService,
  provideLogger,
} from '@o3r/logger';
import {
  ENVIRONMENT_CONFIG_TOKEN,
  provideEnvironment,
} from '@o3r/routing';
import {
  RULES_ENGINE_OPTIONS,
  RulesEngineRunnerModule,
} from '@o3r/rules-engine';
import {
  StorageKeys,
  StorageSync,
} from '@o3r/store-sync';
import {
  AIRPORT_CITY_MAPPER_SERVICE_TOKEN,
  AnalyticsModule,
  AnalyticsOfficeIdModule,
  AnalyticsOfficeIDService,
  AnalyticsService,
  analyticsStorageSync,
  dataLakeStorageSync,
  DEFAULT_DIGITAL_TOUCH_POINT,
  DIGITAL_TOUCH_POINT_TOKEN,
  LAZY_LOADER_SERVICE_TOKEN,
} from '@refx/analytics-core';
import {
  DigitalDataCustomWindowEventEffect,
} from '@refx/analytics-effects/custom-window-event';
import {
  AnalyticsErrorEffect,
} from '@refx/analytics-effects/error';
import {
  AnalyticsWarningEffect,
} from '@refx/analytics-effects/warning';
import {
  OVERRIDE_COMPONENTS_COMMON_RUNTIME_CONFIG,
  REFX_COMPONENTS_BOOKING_RUNTIME_CONFIG_DEFAULT,
  REFX_COMPONENTS_BOOKING_RUNTIME_CONFIG_ID,
} from '@refx/booking-common';
import {
  AppTokenParametersModule,
  AppTokenParametersService,
} from '@refx/booking-services';
import {
  BOOTSTRAP_CONFIGURATION_TOKEN,
  BOT_PROTECTION_FACTORY,
  createErrorsHandlerServiceConfig,
  createRequestParametersConfig,
  CustomTranslationsLoader,
  dateFnsFormatDMY,
  DEFAULT_EXTRA_PLUGINS_BY_API,
  DefaultBotProtectionFactory,
  EXPERIMENT_ANALYTICS_INTERFACE_TOKEN,
  EXTENSION_FEATURE_FLAGS_TOKEN,
  EXTRA_PLUGINS_BY_API_TOKEN,
  GregorianDateFnsAdapter,
  prefersReducedMotion,
  REFX_COMPONENTS_COMMON_RUNTIME_CONFIG_DEFAULT,
  REFX_COMPONENTS_COMMON_RUNTIME_CONFIG_ID,
  refxDialogConfig,
  refxFormFieldOptions,
  refxLabelOptions,
  refxRippleOptions,
  refxTooltipDefaultOptions,
} from '@refx/shared-common';
import {
  AppLayoutModule,
  DebugLinkPresComponent,
  FooterPresModule,
  GifSpinnerModule,
  HeaderContModule,
  LazyOutletModule,
} from '@refx/shared-components';
import {
  aggregateBootstrapConfiguration,
  AirportToCityMapperService,
  AnalyticsUtilityModule,
  AppConfigModule,
  AppInitializationModule,
  AppInitializationService,
  CookieServiceModule,
  DocumentTitleModule,
  DynamicLocaleModule,
  DynamicLocaleService,
  FunctionalContentOverrideRulesEngineActionModule,
  getAppLocaleFactory,
  getRulesEngineOptions,
  LazyLoaderService,
  MultiCurrencyPaymentServiceModule,
  NavigationService,
  OrderChangeTimeoutHelperModule,
  PLUSGRADE_CONTEXT_PROVIDER,
  SelectedDataModule,
  TagsModule,
} from '@refx/shared-services';
import {
  AppConfigStoreModule,
  breadcrumbStorageSync,
  carbonFootprintStorageSync,
  CarbonFootprintStoreModule,
  catalogServicesStorageSync,
  CatalogServicesStoreModule,
  cookiesStorageSync,
  CookiesStoreModule,
  discountsStorageSync,
  metaSearchStorageSync,
  modifySearchStorageSync,
  ModifySearchStoreModule,
  ServerMessagesEffect,
  timeToThinkStorageSync,
  travelerDetailsUIStorageSync,
  uiServicesCatalogueItemsStorageSync,
  uiStatusStorageSync,
  UiStatusStoreModule,
} from '@refx/shared-store';
import dateFnsLocaleEnGB from 'date-fns/locale/en-GB';
import {
  BOOKING_APP_BOOTSTRAP_CONFIG_DEFAULT,
  BOOKING_SERVER_LOGGER_CLIENT_OPTIONS,
  createApisFactoryBooking,
  createPlusgradeContextFactoryBooking,
  DEFAULT_FEATURE_FLAGS,
  getRuntimeChecksConfig,
} from '../configuration/index';
import {
  initializeCustomProviders,
} from '../customization/custom-providers.empty';
import {
  initializeEntryComponents,
  registerCustomComponents,
} from '../customization/presenters-map.empty';
import {
  AnalyticsCartEngagementEffect,
  AnalyticsUpdateUserEffect,
} from '../effects/analytics/index';
import {
  environment,
} from '../environments/environment';
import {
  AppPreferencesFactsModule,
  PortalFactsModule,
  TechnicalDetailsFactsModule,
  UiStatusFactsModule,
} from '../facts/index';
import {
  MetaConnectModule,
} from '../services/index';
import {
  AppComponent,
} from './app.component';
import {
  AppRoutingModule,
} from './app.routing.module';
import {
  AppSkeletonPresComponent,
} from './shared/app-skeleton/index';

/**
 * @param reducer
 */
export function storageSyncWrapper<T, V extends Action = Action>(reducer: ActionReducer<T, V>): ActionReducer<T, V> {
  const storageStates: StorageKeys = [
    {
      airBoundsSearch: airBoundsSearchStorageSync
    },
    {
      cart: cartStorageSync
    },
    {
      checkTasks: checkTasksStorageSync
    },
    {
      configuration: configurationStorageSync
    },
    {
      order: {
        deserialize: orderStorageDeserializer
      }
    },
    {
      insuranceOffers: insuranceOffersStorageSync
    },
    {
      servicesCatalogueItems: servicesCatalogueItemsStorageSync
    },
    {
      catalogServices: catalogServicesStorageSync
    },
    {
      baggagePolicies: baggagePoliciesStorageSync
    },
    {
      airBounds: airBoundsStorageSync
    },
    {
      airBoundExchanges: airBoundExchangesStorageSync
    },
    {
      airBoundExchangesSearch: airBoundExchangesSearchStorageSync
    },
    {
      airOfferConditions: airOfferConditionsStorageSync
    },
    {
      orderChange: orderChangeStorageSync
    },
    {
      airOffers: airOffersStorageSync
    },
    {
      airOffersAdvancedSearch: airOffersAdvancedSearchStorageSync
    },
    {
      breadcrumb: breadcrumbStorageSync
    },
    {
      paymentMethods: paymentMethodsStorageSync
    },
    {
      uiStatus: uiStatusStorageSync
    },
    {
      datalake: dataLakeStorageSync
    },
    {
      travelerDetailsUI: travelerDetailsUIStorageSync
    },
    {
      discounts: discountsStorageSync
    },
    {
      cookies: cookiesStorageSync
    },
    {
      timeToThink: timeToThinkStorageSync
    },
    {
      uiServicesCatalogueItems: uiServicesCatalogueItemsStorageSync
    },
    {
      metaSearch: metaSearchStorageSync
    },
    {
      modifySearch: modifySearchStorageSync
    },
    {
      airOffersUpsell: airOffersUpsellStorageSync
    },
    {
      configOverrides: configOverrideStorageSync
    },
    {
      upgradeCatalogue: upgradeCatalogueItemsStorageSync
    },
    {
      carbonFootprint: carbonFootprintStorageSync
    }
  ];
  return new StorageSync({
    keys: storageStates,
    rehydrate: true,
    storage: sessionStorage || localStorage
  }).localStorageSync()(reducer);
}

/**
 * Analytics reducer for which dates should be kept as strings
 *
 * @param reducer the reducer
 * @returns the sync wrapper to store the analytics in the session or local storage
 */
export function analyticsStorageSyncWrapper<T, V extends Action = Action>(reducer: ActionReducer<T, V>): ActionReducer<T, V> {
  const storageStates: StorageKeys = [
    {
      analytics: analyticsStorageSync
    }
  ];
  return new StorageSync(
    {
      keys: storageStates,
      rehydrate: true,
      storage: sessionStorage || localStorage,
      restoreDates: false
    }
  ).localStorageSync()(reducer);
}

const rootReducers = {
  router: routerReducer
};

const metaReducers = [...(environment.ENABLE_WEBSTORAGE ? [storageSyncWrapper, analyticsStorageSyncWrapper] : [])];

const customPresenters = initializeEntryComponents();
const customProviders: (Provider | EnvironmentProviders)[] = initializeCustomProviders();

/**
 * Configuration reducer specific to Booking to add default global configuration in the initial state of the configuration store
 */
function bookingConfigurationReducer() {
  const initialState = configurationAdapter.upsertMany([{
    id: REFX_COMPONENTS_COMMON_RUNTIME_CONFIG_ID,
    ...REFX_COMPONENTS_COMMON_RUNTIME_CONFIG_DEFAULT,
    ...OVERRIDE_COMPONENTS_COMMON_RUNTIME_CONFIG
  }, {
    id: REFX_COMPONENTS_BOOKING_RUNTIME_CONFIG_ID,
    ...REFX_COMPONENTS_BOOKING_RUNTIME_CONFIG_DEFAULT
  }], configurationAdapter.getInitialState({}));

  return createReducer(
    initialState,
    ...configurationReducerFeatures
  );
}

@NgModule({
  imports: [
    AsyncPipe,
    NgTemplateOutlet,
    HttpClientModule,
    AnalyticsUtilityModule,
    AppSkeletonPresComponent,
    AssetPathOverrideStoreModule,
    AssetRulesEngineActionModule,
    BrowserModule,
    BrowserAnimationsModule.withConfig({
      disableAnimations: prefersReducedMotion()
    }),
    AppSkeletonPresComponent,
    LazyOutletModule,
    DebugLinkPresComponent,
    DynamicLocaleModule,
    AppInitializationModule,
    LocalizationModule.forRoot(),
    TranslateModule.forRoot({
      loader: translateLoaderProvider,
      compiler: {
        provide: TranslateCompiler,
        useClass: TranslateMessageFormatLazyCompiler
      }
    }),
    AppRoutingModule,
    StoreModule.forRoot(rootReducers, {
      metaReducers
    }),
    EffectsModule.forRoot([
      ServerMessagesEffect,
      AnalyticsErrorEffect,
      AnalyticsUpdateUserEffect,
      AnalyticsWarningEffect,
      AnalyticsCartEngagementEffect,
      DigitalDataCustomWindowEventEffect
    ]),
    ConfigurationStoreModule.forRoot(bookingConfigurationReducer),
    MessagesBaseStoreModule,
    LocationStoreModule,
    StoreRouterConnectingModule.forRoot(),
    ...(environment.modules || []),
    AppLayoutModule,
    FooterPresModule,
    HeaderContModule,
    GifSpinnerModule,
    DocumentTitleModule,
    AppConfigStoreModule,
    ConfigurationBaseServiceModule,
    ServicesCatalogueItemsStoreModule,
    AnalyticsModule,
    AnalyticsOfficeIdModule,
    ...customPresenters.customComponentsModules,
    AppTokenParametersModule,
    UiStatusStoreModule,
    PlaceholderRequestStoreModule,
    PlaceholderRulesEngineActionModule,
    ConfigurationRulesEngineActionModule,
    LocalizationRulesEngineActionModule,
    FunctionalContentOverrideRulesEngineActionModule,
    MatNativeDateModule,
    CookiesStoreModule,
    CarbonFootprintStoreModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      // Register the ServiceWorker as soon as the app is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000'
    }),
    TagsModule,
    RulesEngineRunnerModule.forRoot({}),
    PortalFactsModule,
    AppPreferencesFactsModule,
    UiStatusFactsModule,
    TechnicalDetailsFactsModule,
    AppConfigModule,
    CookieServiceModule,
    SelectedDataModule,
    MultiCurrencyPaymentServiceModule,
    ModifySearchStoreModule,
    CatalogServicesStoreModule,
    ConfigOverrideStoreModule,
    ErrorsHandlerModule.forRoot({
      factory: createErrorsHandlerServiceConfig,
      deps: [ENVIRONMENT_CONFIG_TOKEN, BOOTSTRAP_CONFIGURATION_TOKEN]
    }),
    LocalizationOverrideStoreModule,
    PlaceholderModule,
    OrderChangeTimeoutHelperModule,
    OrderChangeStoreModule,
    MetaConnectModule
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  providers: [
    ApiFactoryService,
    provideDynamicContent(),
    provideRequestParameters(createRequestParametersConfig),
    provideLogger(),
    provideEnvironment(environment),
    provideCustomComponents(registerCustomComponents()),
    provideEnvironmentInitializer(() => {
      inject(NavigationService);
    }),
    {
      provide: BOOTSTRAP_CONFIGURATION_TOKEN,
      useFactory: aggregateBootstrapConfiguration(BOOKING_APP_BOOTSTRAP_CONFIG_DEFAULT),
      deps: [
        DOCUMENT,
        RequestParametersService
      ]
    },
    {
      provide: TranslateLoader,
      useClass: CustomTranslationsLoader
    },
    {
      provide: BOT_PROTECTION_FACTORY,
      useClass: DefaultBotProtectionFactory
    },
    {
      provide: API_TOKEN,
      useFactory: createApisFactoryBooking,
      deps: [
        BOOTSTRAP_CONFIGURATION_TOKEN,
        BOT_PROTECTION_FACTORY,
        EventTrackService,
        AppInitializationService,
        Platform,
        AppTokenParametersService,
        AnalyticsOfficeIDService,
        EXTRA_PLUGINS_BY_API_TOKEN,
        [new Optional(), LoggerService]
      ]
    },
    {
      provide: LOG_SERVER_LOGGER_CLIENT_OPTIONS,
      useValue: BOOKING_SERVER_LOGGER_CLIENT_OPTIONS
    },
    {
      provide: AIRPORT_CITY_MAPPER_SERVICE_TOKEN,
      useExisting: AirportToCityMapperService
    },
    {
      provide: LAZY_LOADER_SERVICE_TOKEN,
      useExisting: LazyLoaderService
    },
    {
      provide: USER_RUNTIME_CHECKS,
      useFactory: getRuntimeChecksConfig(environment),
      deps: [DOCUMENT, RequestParametersService]
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        ...refxFormFieldOptions,
        ...refxLabelOptions
      }
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: refxRippleOptions
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: refxDialogConfig
    },
    {
      provide: MESSAGE_FORMAT_CONFIG,
      useValue: {
        enableCache: false,
        ignoreTag: true
      }
    },
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: refxTooltipDefaultOptions
    },
    {
      provide: EVENT_TRACK_SERVICE_CONFIGURATION,
      useValue: {
        useBrowserApiForFirstFP: true
      }
    },
    {
      provide: CUSTOM_LOCALIZATION_CONFIGURATION_TOKEN,
      useFactory: getAppLocaleFactory(environment),
      deps: [RequestParametersService, DynamicLocaleService]
    },
    {
      provide: EXTENSION_FEATURE_FLAGS_TOKEN,
      useValue: DEFAULT_FEATURE_FLAGS,
      multi: true
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: dateFnsFormatDMY
    },
    {
      provide: MAT_DATE_LOCALE,
      useValue: dateFnsLocaleEnGB
    },
    {
      provide: DateAdapter,
      useClass: GregorianDateFnsAdapter
    },
    {
      provide: RULES_ENGINE_OPTIONS,
      useFactory: getRulesEngineOptions,
      deps: [DOCUMENT, RequestParametersService]
    },
    {
      provide: Window,
      useValue: window
    },
    {
      provide: EXTRA_PLUGINS_BY_API_TOKEN,
      useValue: DEFAULT_EXTRA_PLUGINS_BY_API
    },
    {
      provide: EXPERIMENT_ANALYTICS_INTERFACE_TOKEN,
      useExisting: AnalyticsService
    },
    {
      provide: DIGITAL_TOUCH_POINT_TOKEN,
      useValue: DEFAULT_DIGITAL_TOUCH_POINT
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (appInitializationService: AppInitializationService, configService: ConfigurationBaseService, analyticsService: AnalyticsService) => async () => {
        await appInitializationService.getDynamicConfig(configService);
        analyticsService.setup();
      },
      deps: [AppInitializationService, ConfigurationBaseService, AnalyticsService],
      multi: true
    },
    {
      provide: PLUSGRADE_CONTEXT_PROVIDER,
      useFactory: createPlusgradeContextFactoryBooking,
      deps: [Store]
    },
    ...customProviders
  ]
})
export class AppModule {}
